/* eslint-disable no-unused-vars, array-callback-return, import/no-anonymous-default-export */
import React from 'react';
import PropTypes from 'prop-types';
import { PivotData } from './Utilities';
import { naturalSort } from './Utilities';
import update from 'immutability-helper';

// helper function for setting row/col-span in pivotTableRenderer
const spanSize = function(arr, i, j) {
  return 1; // Always return 1 for fixed rowSpan
};

const orders = {
  desc: {
    label: 'desc',
    fn: (a, b) => -naturalSort(a, b)
  },
  asc: {
    label: 'asc',
    fn: naturalSort
  }
}

const moneyLikeFields = new Set([
  'debit',
  'credit',
  'debit_credit_amount',
  'total',
]);

const analyticsIdentifierFields = new Set([
  'deviceid',
]);

const isAnalyticsIdentifierField = attrName =>
  attrName === 'id' ||
  attrName.endsWith('_id') ||
  analyticsIdentifierFields.has(attrName);

const numericSortFields = new Set([
  'id',
  'debit',
  'credit',
  'debit_credit_amount',
  'total',
]);

const parseNumericSortValue = value => {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }

  const normalized = String(value).replace(/,/g, '').trim();
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
};

const nonSummableNumericFields = new Set([
  'id',
  'account_id',
  'gl_number',
  'transaction_id',
]);

const shouldTotalFieldName = attrName => {
  const normalizedName = String(attrName || '')
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_');
  return (
    normalizedName === 'debit' ||
    normalizedName === 'credit' ||
    normalizedName === 'debit_credit_amount' ||
    normalizedName.includes('amount') ||
    normalizedName.includes('surcharge') ||
    normalizedName.includes('discount') ||
    normalizedName.includes('sale') ||
    normalizedName.includes('tax') ||
    normalizedName.includes('tip') ||
    normalizedName.startsWith('total')
  );
};

const parseDisplayNumber = value => {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }

  const rawValue = String(value).trim();
  if (!rawValue || /^(null|undefined)$/i.test(rawValue)) {
    return null;
  }

  const isNegativeAccountingValue = /^\(.*\)$/.test(rawValue);
  const normalized = rawValue
    .replace(/[,$\s]/g, '')
    .replace(/[()]/g, '');
  if (!normalized || /^-?\d{4}-\d{2}-\d{2}$/.test(normalized)) {
    return null;
  }

  const parsed = Number(normalized);
  if (!Number.isFinite(parsed)) {
    return null;
  }

  return isNegativeAccountingValue ? -parsed : parsed;
};

const shouldTotalRowAttr = (attrName, values) => {
  if (
    nonSummableNumericFields.has(attrName) ||
    !shouldTotalFieldName(attrName)
  ) {
    return false;
  }

  const populatedValues = values.filter(
    value =>
      value !== null &&
      value !== undefined &&
      String(value).trim() !== '' &&
      !/^(null|undefined)$/i.test(String(value).trim())
  );

  return (
    populatedValues.length > 0 &&
    populatedValues.every(value => parseDisplayNumber(value) !== null)
  );
};

const getFilteredRawRecords = pivotData => {
  const records = [];

  PivotData.forEachRecord(
    pivotData.props.data,
    pivotData.props.derivedAttributes,
    record => {
      if (pivotData.filter(record)) {
        records.push(record);
      }
    }
  );

  return records;
};

const formatTotalValue = (attrName, value) => {
  if (!Number.isFinite(value)) {
    return null;
  }

  return value.toFixed(2);
};

const isNumericDisplayValue = value => parseDisplayNumber(value) !== null;

const isRowFieldName = attrName =>
  String(attrName || '').trim().toLowerCase() === 'row';

const isSageRowFieldName = attrName =>
  String(attrName || '').trim().toLowerCase() === 'sage_row';

const normalizeFieldName = attrName =>
  String(attrName || '')
    .trim()
    .toLowerCase()
    .replace(/[.\s-]+/g, '_')
    .replace(/^_+|_+$/g, '');

const leftAlignedRowFields = new Set([
  'account',
  'account_id',
  'account_description',
  'account_no',
  'class_department_location',
  'nominal_a/c_ref',
  'posting_comment',
]);

const isLeftAlignedRowFieldName = attrName =>
  leftAlignedRowFields.has(normalizeFieldName(attrName));

const shouldSplitPositiveNegativeTotals = (rowAttrs, reportName) => {
  const normalizedReportName = String(reportName || '').toLowerCase();
  return (
    rowAttrs.some(isRowFieldName) ||
    normalizedReportName.includes('imp') ||
    normalizedReportName.includes('netsuite') ||
    normalizedReportName.includes('yardi') ||
    normalizedReportName.includes('business')
  );
};

const splitPositiveNegativeTotals = values =>
  values.reduce(
    (totals, value) => {
      const numberValue = Number(value);
      if (!Number.isFinite(numberValue)) {
        return totals;
      }

      if (numberValue >= 0) {
        totals.positive += numberValue;
      } else {
        totals.negative += numberValue;
      }

      return totals;
    },
    { positive: 0, negative: 0 }
  );

const compareRowValues = (attrName, aValue, bValue, order) => {
  if (numericSortFields.has(attrName)) {
    const numericA = parseNumericSortValue(aValue);
    const numericB = parseNumericSortValue(bValue);

    if (numericA !== null && numericB !== null) {
      return order === orders.asc.label ? numericA - numericB : numericB - numericA;
    }
  }

  return order === orders.asc.label
    ? naturalSort(aValue, bValue)
    : -naturalSort(aValue, bValue);
};

const formatDisplayValue = (attrName, value, reportName = '') => {
  if (value === null || value === undefined || value === '') {
    return value;
  }

  const normalizedAttrName = normalizeFieldName(attrName);
  const isAnalyticsReport = String(reportName).toLowerCase().includes('analytics');
  const shouldFormatNumber =
    moneyLikeFields.has(normalizedAttrName) ||
    (isAnalyticsReport && !isAnalyticsIdentifierField(normalizedAttrName));
  const numericValue = parseDisplayNumber(value);

  if (shouldFormatNumber && numericValue !== null) {
    return numericValue.toFixed(2);
  }

  return value;
};

function redColorScaleGenerator(values) {
  const min = Math.min.apply(Math, values);
  const max = Math.max.apply(Math, values);
  return x => {
    // eslint-disable-next-line no-magic-numbers
    const nonRed = 255 - Math.round((255 * (x - min)) / (max - min));
    return { backgroundColor: `rgb(255,${nonRed},${nonRed})` };
  };
}

function makeRenderer(opts = {}) {
  class TableRenderer extends React.PureComponent {
    render() {
      const pivotData = new PivotData(this.props);
      const colAttrs = pivotData.props.cols;
      const rowAttrs = pivotData.props.rows;
      let rowKeys = pivotData.getRowKeys();
      const colKeys = pivotData.getColKeys();
      const grandTotalAggregator = pivotData.getAggregator([], []);
      const sorters = pivotData.props.sorters;
      const sortRow = pivotData.props.enableRowSort;
      const sortCol = pivotData.props.enableColSort;
      const rawRecords = getFilteredRawRecords(pivotData);
      const hasSplitTotals = shouldSplitPositiveNegativeTotals(
        rowAttrs,
        this.props.reportName
      );
      const rowAttrTotals = rowAttrs.reduce((totals, attrName, attrIndex) => {
        const normalizedAttrName = normalizeFieldName(attrName);
        const values = normalizedAttrName.includes('surcharge')
          ? rowKeys.map(rowKey => rowKey[attrIndex])
          : rawRecords.map(record =>
              attrName in record ? record[attrName] : null
            );

        if (shouldTotalRowAttr(attrName, values)) {
          totals[attrName] = values.reduce((sum, value) => {
            const numberValue = parseDisplayNumber(value);
            return numberValue === null ? sum : sum + numberValue;
          }, 0);
        }

        return totals;
      }, {});
      const totalLabelIndex = rowAttrs.findIndex(
        attrName => !(attrName in rowAttrTotals)
      );
      const rowHeaderColTotals = colKeys.reduce((totals, colKey) => {
        totals[colKey.join(String.fromCharCode(0))] = splitPositiveNegativeTotals(
          rowKeys.map(rowKey => pivotData.getAggregator(rowKey, colKey).value())
        );
        return totals;
      }, {});
      const rowHeaderGrandTotals = splitPositiveNegativeTotals(
        rowKeys.map(rowKey => pivotData.getAggregator(rowKey, []).value())
      );

      const sorterCol = this.props.sorterCol;
      const rowSorter = this.props.rowSorter;
      const compareValues = (aValue, bValue, order) => {
        const numericA = Number(aValue);
        const numericB = Number(bValue);

        if (Number.isFinite(numericA) && Number.isFinite(numericB)) {
          return order === orders.asc.label ? numericA - numericB : numericB - numericA;
        }

        return order === orders.asc.label
          ? naturalSort(aValue, bValue)
          : -naturalSort(aValue, bValue);
      };

      if (sorterCol?.name && sorterCol?.order) {
        const { name, order } = sorterCol;
        const selectedColKey = name.split(String.fromCharCode(0));

        rowKeys = [...rowKeys].sort((rowA, rowB) => {
          const aValue = pivotData.getAggregator(rowA, selectedColKey).value();
          const bValue = pivotData.getAggregator(rowB, selectedColKey).value();
          const comparison = compareValues(aValue, bValue, order);

          if (comparison !== 0) {
            return comparison;
          }

          return naturalSort(rowA.join(' | '), rowB.join(' | '));
        });
      }

      if (rowSorter?.name && rowSorter?.order) {
        const rowAttrIndex = rowAttrs.indexOf(rowSorter.name);

        if (rowAttrIndex >= 0) {
          rowKeys = [...rowKeys].sort((rowA, rowB) => {
            const comparison = compareRowValues(
              rowSorter.name,
              rowA[rowAttrIndex],
              rowB[rowAttrIndex],
              rowSorter.order
            );

            if (comparison !== 0) {
              return comparison;
            }

            return naturalSort(rowA.join(' | '), rowB.join(' | '));
          });
        }
      }

      let valueCellColors = () => { };
      let rowTotalColors = () => { };
      let colTotalColors = () => { };
      if (opts.heatmapMode) {
        const colorScaleGenerator = this.props.tableColorScaleGenerator;
        const rowTotalValues = colKeys.map(x =>
          pivotData.getAggregator([], x).value()
        );
        rowTotalColors = colorScaleGenerator(rowTotalValues);
        const colTotalValues = rowKeys.map(x =>
          pivotData.getAggregator(x, []).value()
        );
        colTotalColors = colorScaleGenerator(colTotalValues);

        if (opts.heatmapMode === 'full') {
          const allValues = [];
          rowKeys.map(r =>
            colKeys.map(c =>
              allValues.push(pivotData.getAggregator(r, c).value())
            )
          );
          const colorScale = colorScaleGenerator(allValues);
          valueCellColors = (r, c, v) => colorScale(v);
        } else if (opts.heatmapMode === 'row') {
          const rowColorScales = {};
          rowKeys.map(r => {
            const rowValues = colKeys.map(x =>
              pivotData.getAggregator(r, x).value()
            );
            rowColorScales[r] = colorScaleGenerator(rowValues);
          });
          valueCellColors = (r, c, v) => rowColorScales[r](v);
        } else if (opts.heatmapMode === 'col') {
          const colColorScales = {};
          colKeys.map(c => {
            const colValues = rowKeys.map(x =>
              pivotData.getAggregator(x, c).value()
            );
            colColorScales[c] = colorScaleGenerator(colValues);
          });
          valueCellColors = (r, c, v) => colColorScales[c](v);
        }
      }

      const joiner = String.fromCharCode(0);
      const getActiveRowOrder = key => {
        if (rowSorter?.name === key) {
          return rowSorter.order;
        }
        return null;
      };

      const getNextRowOrder = key => {
        const activeOrder = getActiveRowOrder(key);
        if (activeOrder === orders.asc.label) {
          return orders.desc;
        }
        if (activeOrder === orders.desc.label) {
          return null;
        }
        return orders.asc;
      };

      const getNextColOrder = colKey => {
        const valKey = colKey.join(joiner);
        if (sorterCol?.name === valKey && sorterCol?.order === orders.asc.label) {
          return orders.desc.label;
        }
        if (sorterCol?.name === valKey && sorterCol?.order === orders.desc.label) {
          return null;
        }
        return orders.asc.label;
      };

      const getSortRowClasses = (order, key) => {
        let classes = 'sorterIcon triangle ';
        if (order === orders.desc) {
          classes += 'rotate180 '
        }
        if (getActiveRowOrder(key) === order.label) {
          classes += 'activeSorterIcon'
        }
        return classes;
      }

      const getSortedColClasses = (order, key, colKey) => {
        const keyStr = colKey.join(joiner);
        const sorter = this.props.sorterCol;
        let classes = 'sorterIcon triangle ';
        if (order === orders.desc.label) {
          classes += 'rotate180 ';
        }
        if (sorter && sorter.name === keyStr && sorter.order === order) {
          classes += 'activeSorterIcon';
        }
        return classes;
      }

      const handleRowClick = (order = orders.asc, key) => {
        this.props.onChange(update(this.props, {
          rowSorter: { $set: order ? { name: key, order: order.label } : null },
          sorters: { $set: {} },
          sorterCol: { $set: null },
        }))
      }

      const updateSorterCol = val => {
        this.props.onChange(update(this.props, {
          sorterCol: { $set: val },
          rowSorter: { $set: null },
          // only one works
          sorters: { $set: {} }
        }))
      }

      const handleColClick = (keys = [], key, order = orders.asc.label) => {
        const rows = pivotData.props.rows;
        if (rows.length < 1) { return }
        const valKey = keys.join(joiner);
      
        if (!order) {
          updateSorterCol(null);
        } else {
          updateSorterCol({ name: valKey, order });
        }
      };
      

      const getClickHandler =
        this.props.tableOptions && this.props.tableOptions.clickCallback
          ? (value, rowValues, colValues) => {
              const filters = {};
              for (const i of Object.keys(colAttrs || {})) {
                const attr = colAttrs[i];
                if (colValues[i] !== null) {
                  filters[attr] = colValues[i];
                }
              }
              for (const i of Object.keys(rowAttrs || {})) {
                const attr = rowAttrs[i];
                if (rowValues[i] !== null) {
                  filters[attr] = rowValues[i];
                }
              }
              return e =>
                this.props.tableOptions.clickCallback(
                  e,
                  value,
                  filters,
                  pivotData
                );
            }
          : null;

      const activeReportName = this.props.reportName;

      return (
        <table className="pvtTable">
          <thead>
            {colAttrs.map(function(c, j) {
              return (
                <tr key={`colAttr${j}`}>
                  {j === 0 && rowAttrs.length !== 0 && (
                    <th colSpan={rowAttrs.length} rowSpan={colAttrs.length} />
                  )}
                  <th className="pvtAxisLabel">{c}</th>
                  {colKeys.map(function(colKey, i) {
                    return (
                      <th
                        className="pvtColLabel"
                        key={`colKey${i}`}
                        colSpan={1}
                        rowSpan={
                          j === colAttrs.length - 1 && rowAttrs.length !== 0
                            ? 2
                            : 1
                        }
                        onClick={() =>
                          sortCol &&
                          colKey &&
                          colKey.indexOf(colKey[j]) === colKey.length - 1 &&
                          handleColClick(colKey, colKey[j], getNextColOrder(colKey))
                        }
                        style={{
                          cursor:
                            sortCol &&
                            colKey &&
                            colKey.indexOf(colKey[j]) === colKey.length - 1
                              ? 'pointer'
                              : 'default'
                        }}
                      >
                        {formatDisplayValue(c, colKey[j], activeReportName)}
                            {
                              (sortCol && colKey && colKey.indexOf(colKey[j]) === colKey.length - 1) ?
                                (<div className='sorters'>
                                  <div className={getSortedColClasses(orders.asc.label, colKey[j], colKey)}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleColClick(colKey, colKey[j], orders.asc.label);
                                    }}></div>
                                  <div className={getSortedColClasses(orders.desc.label, colKey[j], colKey)}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleColClick(colKey, colKey[j], orders.desc.label);
                                    }}></div>
                                </div>) : null
                            }
                      </th>
                    );
                  })}

                  {j === 0 && (
                    <th
                      className="pvtTotalLabel"
                      rowSpan={
                        colAttrs.length + (rowAttrs.length === 0 ? 0 : 1)
                      }
                    >
                      Totals
                    </th>
                  )}
                </tr>
              );
            })}

                {rowAttrs.length !== 0 && (
                <tr>
                  {rowAttrs.map(function (r, i) {
                    return (
                      <th
                        className="pvtAxisLabel"
                        key={`rowAttr${i}`}
                        onClick={() => sortRow && handleRowClick(getNextRowOrder(r), r)}
                        style={{
                          cursor: sortRow ? 'pointer' : 'default',
                          textAlign:
                            isSageRowFieldName(r) || isLeftAlignedRowFieldName(r)
                              ? 'left'
                              : undefined,
                        }}
                      >
                        <div
                          className="centered-sorters-box"
                          style={{
                            justifyContent:
                              isSageRowFieldName(r) || isLeftAlignedRowFieldName(r)
                              ? 'flex-start'
                              : undefined,
                          }}
                        >
                          {r}
                          {sortRow ? (
                            <div className='sorters'>
                              <div className={getSortRowClasses(orders.asc, r)}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRowClick(orders.asc, r);
                                }}></div>
                              <div className={getSortRowClasses(orders.desc, r)}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRowClick(orders.desc, r);
                                }}></div>
                            </div>
                          ) : null}
                        </div>
                      </th>
                    );
                  })}
                  <th className="pvtTotalLabel">
                    {colAttrs.length === 0 ? 'Totals' : null}
                  </th>
                </tr>
              )}
          </thead>

          <tbody>
            {rowKeys.map(function(rowKey, i) {
              const totalAggregator = pivotData.getAggregator(rowKey, []);
              return (
                <tr key={`rowKeyRow${i}`}>
                  {rowKey.map(function(txt, j) {
                    return (
                      <th
                        key={`rowKeyLabel${i}-${j}`}
                        className="pvtRowLabel"
                        rowSpan={1}
                        colSpan={
                          j === rowAttrs.length - 1 && colAttrs.length !== 0
                            ? 2
                            : 1
                        }
                        style={{
                          textAlign: isSageRowFieldName(rowAttrs[j]) ||
                            isLeftAlignedRowFieldName(rowAttrs[j])
                            ? 'left'
                            : isNumericDisplayValue(txt)
                              ? 'right'
                              : undefined,
                        }}
                      >
                        {formatDisplayValue(rowAttrs[j], txt, activeReportName)}
                      </th>
                    );
                  })}
                  {colKeys.map(function(colKey, j) {
                    const aggregator = pivotData.getAggregator(rowKey, colKey);
                    return (
                      <td
                        className="pvtVal"
                        key={`pvtVal${i}-${j}`}
                        onClick={
                          getClickHandler &&
                          getClickHandler(aggregator.value(), rowKey, colKey)
                        }
                        style={valueCellColors(
                          rowKey,
                          colKey,
                          aggregator.value()
                        )}
                      >
                        {aggregator.format(aggregator.value())}
                      </td>
                    );
                  })}
                  <td
                    className="pvtTotal"
                    onClick={
                      getClickHandler &&
                      getClickHandler(totalAggregator.value(), rowKey, [null])
                    }
                    style={colTotalColors(totalAggregator.value())}
                  >
                    {totalAggregator.format(totalAggregator.value())}
                  </td>
                </tr>
              );
            })}

            {hasSplitTotals ? (
              <>
                <tr>
                  {rowAttrs.length > 0 ? (
                    rowAttrs.map((attrName, attrIndex) => {
                      const totalValue = rowAttrTotals[attrName];

                      return (
                        <th
                          className="pvtTotalLabel"
                          key={`rowAttrTotal${attrName}`}
                          style={{
                            textAlign:
                              attrIndex !== totalLabelIndex &&
                              Number.isFinite(totalValue)
                                ? 'right'
                                : undefined,
                          }}
                        >
                          {attrIndex === totalLabelIndex
                            ? 'Totals'
                            : formatTotalValue(attrName, totalValue)}
                        </th>
                      );
                    })
                  ) : (
                    <th className="pvtTotalLabel">Totals</th>
                  )}

                  {colAttrs.length !== 0 && <th className="pvtTotalLabel" />}

                  {colKeys.map(function(colKey, i) {
                    const totalAggregator = pivotData.getAggregator([], colKey);
                    return (
                      <td
                        className="pvtTotal"
                        key={`total${i}`}
                        onClick={
                          getClickHandler &&
                          getClickHandler(totalAggregator.value(), [null], colKey)
                        }
                        style={rowTotalColors(totalAggregator.value())}
                      >
                        {totalAggregator.format(totalAggregator.value())}
                      </td>
                    );
                  })}

                  <td
                    onClick={
                      getClickHandler &&
                      getClickHandler(grandTotalAggregator.value(), [null], [null])
                    }
                    className="pvtGrandTotal"
                  >
                    {grandTotalAggregator.format(grandTotalAggregator.value())}
                  </td>
                </tr>

                {['positive', 'negative'].map(totalType => {
                  const label =
                    totalType === 'positive' ? 'Total Debit' : 'Total Credit';

                  return (
                    <tr key={`rowHeader${totalType}Totals`}>
                      {rowAttrs.length > 0 ? (
                        rowAttrs.map((attrName, attrIndex) => (
                          <th
                            className="pvtTotalLabel"
                            key={`rowHeader${totalType}Total${attrName}`}
                          >
                            {attrIndex === 0 ? label : null}
                          </th>
                        ))
                      ) : (
                        <th className="pvtTotalLabel">{label}</th>
                      )}

                      {colAttrs.length !== 0 && <th className="pvtTotalLabel" />}

                      {colKeys.map(function(colKey, i) {
                        const colTotal =
                          rowHeaderColTotals[colKey.join(String.fromCharCode(0))]?.[
                            totalType
                          ] || 0;
                        return (
                          <td className="pvtTotal" key={`rowHeader${totalType}Total${i}`}>
                            {grandTotalAggregator.format(colTotal)}
                          </td>
                        );
                      })}

                      <td className="pvtGrandTotal">
                        {grandTotalAggregator.format(rowHeaderGrandTotals[totalType])}
                      </td>
                    </tr>
                  );
                })}
              </>
            ) : (
              <tr>
                {rowAttrs.length > 0 ? (
                  rowAttrs.map((attrName, attrIndex) => {
                    const totalValue = rowAttrTotals[attrName];

                    return (
                      <th
                        className="pvtTotalLabel"
                        key={`rowAttrTotal${attrName}`}
                        style={{
                          textAlign:
                            attrIndex !== totalLabelIndex &&
                            Number.isFinite(totalValue)
                              ? 'right'
                              : undefined,
                        }}
                      >
                        {attrIndex === totalLabelIndex
                          ? 'Totals'
                          : formatTotalValue(attrName, totalValue)}
                      </th>
                    );
                  })
                ) : (
                  <th className="pvtTotalLabel">Totals</th>
                )}

                {colAttrs.length !== 0 && <th className="pvtTotalLabel" />}

                {colKeys.map(function(colKey, i) {
                  const totalAggregator = pivotData.getAggregator([], colKey);
                  return (
                    <td
                      className="pvtTotal"
                      key={`total${i}`}
                      onClick={
                        getClickHandler &&
                        getClickHandler(totalAggregator.value(), [null], colKey)
                      }
                      style={rowTotalColors(totalAggregator.value())}
                    >
                      {totalAggregator.format(totalAggregator.value())}
                    </td>
                  );
                })}

                <td
                  onClick={
                    getClickHandler &&
                    getClickHandler(grandTotalAggregator.value(), [null], [null])
                  }
                  className="pvtGrandTotal"
                >
                  {grandTotalAggregator.format(grandTotalAggregator.value())}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      );
    }
  }

  TableRenderer.defaultProps = PivotData.defaultProps;
  TableRenderer.propTypes = PivotData.propTypes;
  //TableRenderer.defaultProps.tableColorScaleGenerator = redColorScaleGenerator;
  //TableRenderer.defaultProps.tableOptions = {};
  //TableRenderer.propTypes.tableColorScaleGenerator = PropTypes.func;
  //TableRenderer.propTypes.tableOptions = PropTypes.object;
  return TableRenderer;
}

class TSVExportRenderer extends React.PureComponent {
  render() {
    const pivotData = new PivotData(this.props);
    const rowKeys = pivotData.getRowKeys();
    const colKeys = pivotData.getColKeys();
    if (rowKeys.length === 0) {
      rowKeys.push([]);
    }
    if (colKeys.length === 0) {
      colKeys.push([]);
    }

    const headerRow = pivotData.props.rows.map(r => r);
    if (colKeys.length === 1 && colKeys[0].length === 0) {
      headerRow.push(this.props.aggregatorName);
    } else {
      colKeys.map(c => headerRow.push(c.join('-')));
    }

    const result = rowKeys.map(r => {
      const row = r.map(x => x);
      colKeys.map(c => {
        const v = pivotData.getAggregator(r, c).value();
        row.push(v ? v : '');
      });
      return row;
    });

    result.unshift(headerRow);

    return (
      <textarea
        value={result.map(r => r.join('\t')).join('\n')}
        style={{ width: window.innerWidth / 2, height: window.innerHeight / 2 }}
        readOnly={true}
      />
    );
  }
}

TSVExportRenderer.defaultProps = PivotData.defaultProps;
TSVExportRenderer.propTypes = PivotData.propTypes;

export default {
  Table: makeRenderer(),
  'Table Heatmap': makeRenderer({ heatmapMode: 'full' }),
  'Table Col Heatmap': makeRenderer({ heatmapMode: 'col' }),
  'Table Row Heatmap': makeRenderer({ heatmapMode: 'row' }),
  'Exportable TSV': TSVExportRenderer,
};
